'use client';

import { useCart } from '@/features/checkout/CartProvider';
import FilterBar from '@/features/filter-bar/FilterBar';
import SearchBtn from '@/features/search-input/SearchBtn.jsx';
import { clientFetch } from '@/lib/api/clientFetch';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import Link from 'next/link';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useState, useTransition } from 'react';
import {
  Badge,
  Button,
  Image,
  Nav,
  Navbar,
  NavDropdown,
  Offcanvas,
} from 'react-bootstrap';
import '../../filter-bar/filterBar.scss';

import AdminNavMenu from './AdminNavMenu.jsx';
import LogoCluster from './LogoCluster.jsx';
import { navItemVariants } from './animations.js';
import { getNavRouteFlags } from './navRouteFlags.js';
import { resolveHeaderMessage } from './resolveHeaderMessage.js';
import useUnreadInboxCount from './useUnreadInboxCount.js';

function buildCurrentPath(pathname, searchParams) {
  const query = searchParams?.toString();
  return query ? `${pathname}?${query}` : pathname;
}

export default function NavbarLG({
  currentUser = null,
  locale,
  headerMessages = {},
  languageOptionLabels = {},
  categoriesTree = [],
  categoriesMessages = {},
  brandName,
}) {
  const reduceMotion = useReducedMotion();
  const pathname = usePathname() || '/';
  const searchParams = useSearchParams();
  const router = useRouter();
  const [isPending, startTransition] = useTransition();
  const { totalItems, hasHydrated, resetCart } = useCart();
  const [showSearchInput, setShowSearchInput] = useState(false);
  const [shopExpanded, setShopExpanded] = useState(false);
  const [logoutError, setLogoutError] = useState('');

  const { isInHubRoute, isLandingRoute, isInShopRoute, isRestrictedScreen } =
    getNavRouteFlags(pathname);
  const isInAdminRoute = pathname.startsWith('/admin');
  const isAdmin = ['admin', 'superadmin'].includes(currentUser?.role);
  const displayName =
    currentUser?.hubProfile?.displayName || currentUser?.name || '';
  const profilePath = isInHubRoute ? '/hub/profile' : '/account';
  const cartCount = hasHydrated ? totalItems : 0;
  const currentPath = buildCurrentPath(pathname, searchParams);
  const { unreadCount, unreadBadgeText } = useUnreadInboxCount(isAdmin);

  const labels = useMemo(
    () => ({
      homeLink: resolveHeaderMessage(
        headerMessages,
        'aria.homeLink',
        'Go to homepage',
      ),
      languageSwitcher: resolveHeaderMessage(
        headerMessages,
        'aria.languageSwitcher',
        'Change language',
      ),
      shop: resolveHeaderMessage(headerMessages, 'nav.shop', 'Shop'),
      hub: resolveHeaderMessage(headerMessages, 'nav.hub', 'Hub'),
      cart: resolveHeaderMessage(headerMessages, 'nav.cart', 'Cart'),
      signIn: resolveHeaderMessage(headerMessages, 'nav.signin', 'Sign In'),
      searchTitle: resolveHeaderMessage(
        headerMessages,
        'search.searchTitle',
        'Search',
      ),
      searchPlaceholder: resolveHeaderMessage(
        headerMessages,
        'search.placeholder',
        'Search products...',
      ),
      profile: resolveHeaderMessage(
        headerMessages,
        'nav.dropdown.profile',
        'Profile',
      ),
      logout: resolveHeaderMessage(
        headerMessages,
        'nav.dropdown.logout',
        'Logout',
      ),
      logoutError: resolveHeaderMessage(
        headerMessages,
        'nav.logoutError',
        'Could not log out user! Something went wrong.',
      ),
      inbox: resolveHeaderMessage(
        headerMessages,
        'nav.dropdown.inbox',
        'Inbox',
      ),
    }),
    [headerMessages],
  );

  useEffect(() => {
    setShopExpanded(isInShopRoute);
  }, [isInShopRoute]);

  const showShopExtras = isInShopRoute && shopExpanded && !isInHubRoute;
  const showHubShopBtn = isInHubRoute && !showShopExtras && !isInAdminRoute;
  const showConnectExtras = showShopExtras || isInAdminRoute;

  useEffect(() => {
    if (!showConnectExtras && showSearchInput) {
      setShowSearchInput(false);
    }
  }, [showConnectExtras, showSearchInput]);

  const goToPath = (path) => {
    setShowSearchInput(false);
    setLogoutError('');
    startTransition(() => {
      router.push(path);
    });
  };

  const onShopClick = () => {
    if (!isInShopRoute) {
      setShopExpanded(true);
      goToPath('/shop');
      return;
    }

    setShopExpanded((current) => !current);
  };

  const handleLogoClick = (event) => {
    event?.preventDefault?.();

    if (isInShopRoute) {
      const nextPath =
        pathname === '/shop' && !searchParams?.toString() ? '/' : '/shop';
      setShopExpanded(nextPath === '/shop');
      goToPath(nextPath);
      return;
    }

    if (isInHubRoute) {
      goToPath(pathname === '/hub' ? '/' : '/hub');
      return;
    }

    if (pathname.startsWith('/account')) {
      goToPath('/shop');
      return;
    }

    goToPath('/');
  };

  const handleLogout = async () => {
    setLogoutError('');

    try {
      await clientFetch('/api/users/logout', {
        method: 'POST',
      });

      resetCart();
      setShowSearchInput(false);
      startTransition(() => {
        router.replace('/');
        router.refresh();
      });
    } catch {
      setLogoutError(labels.logoutError);
    }
  };

  const cartLabel =
    cartCount > 0 ? `${labels.cart} (${cartCount})` : labels.cart;
  const userLabel = displayName ? displayName.split(' ')[0] : labels.signIn;

  const handleSearchClick = () => setShowSearchInput(true);

  return (
    <>
      <Navbar
        variant="dark"
        className={`d-flex justify-content-between align-items-center large-navbar ${
          showSearchInput ? 'search-active' : ''
        }`}
        style={{ width: '100%', flexWrap: 'nowrap' }}
      >
        <LogoCluster
          rasterImageSrc="/icons/generic-game-desktop.png"
          onHomeClick={handleLogoClick}
          siteName={String(headerMessages.siteName || '').replace(
            '{{brandName}}',
            brandName,
          )}
          siteNameShort={headerMessages.siteNameShort || 'GameHub'}
          siteNameXs={headerMessages.siteNameXs || 'GameHub'}
          wordmarkPalette={
            locale === 'fr-SN' ? 'palette-country-sn' : 'palette-country-us'
          }
          showLangSwitcher={!isRestrictedScreen}
          currentLocale={locale}
          languageOptionLabels={languageOptionLabels}
          languageSwitcherAriaLabel={labels.languageSwitcher}
          homeAriaLabel={labels.homeLink}
          brandName={brandName}
          className="ps-1"
        />

        {showShopExtras || showHubShopBtn ? (
          <AnimatePresence initial={false} mode="wait">
            <motion.span
              key={showShopExtras ? 'hub-btn-lg' : 'shop-btn-lg'}
              initial={{ opacity: 0, y: reduceMotion ? 0 : -6 }}
              animate={{ opacity: 1, y: 0, transition: { duration: 0.32 } }}
              exit={{
                opacity: 0,
                y: reduceMotion ? 0 : -4,
                pointerEvents: 'none',
                transition: { duration: 0.18 },
              }}
              layout
            >
              {showShopExtras ? (
                <Button
                  variant="info"
                  onClick={() => {
                    setShopExpanded(false);
                    goToPath('/hub');
                  }}
                  active={isInHubRoute}
                  className="nav-btn"
                >
                  <span aria-hidden="true">🎮</span>
                  {labels.hub}
                </Button>
              ) : (
                <Button
                  variant="warning"
                  onClick={onShopClick}
                  active={isInShopRoute && !isInHubRoute}
                  className="nav-btn"
                >
                  <img src="/icons/store.png" alt="" width="18" height="18" />
                  <span>{labels.shop}</span>
                </Button>
              )}
            </motion.span>
          </AnimatePresence>
        ) : null}

        <Nav className="flex-row gap-1 justify-content-end align-items-center flex-grow-0 nav-links">
          <AnimatePresence initial={false} mode="popLayout">
            {!showConnectExtras ? (
              <motion.div
                key="shop-hub-cluster-lg"
                variants={navItemVariants}
                initial="hidden"
                animate="show"
                exit="exit"
                layout
              >
                <div className="d-flex gap-1 align-items-center mt-1">
                  {!isInHubRoute ? (
                    <Button
                      variant="info"
                      onClick={() => {
                        setShopExpanded(false);
                        goToPath('/hub');
                      }}
                      active={isInHubRoute}
                      className="nav-btn"
                    >
                      <span aria-hidden="true">🎮</span>
                      {labels.hub}
                    </Button>
                  ) : null}

                  {!isInShopRoute && !isInHubRoute ? (
                    <Button
                      variant="warning"
                      onClick={onShopClick}
                      active={
                        (isLandingRoute || isInShopRoute) && !isInHubRoute
                      }
                      className="nav-btn"
                    >
                      <img
                        src="/icons/store.png"
                        alt=""
                        width="18"
                        height="18"
                      />
                      <span>{labels.shop}</span>
                    </Button>
                  ) : null}

                  {isInHubRoute ? (
                    currentUser ? (
                      <NavDropdown
                        title={userLabel}
                        id="username-hub"
                        className="ps-0 my-0 fs-6"
                      >
                        {isAdmin ? (
                          <>
                            <NavDropdown.Item
                              as={Link}
                              href="/admin/inbox"
                              className="d-flex align-items-center gap-2"
                            >
                              <span aria-hidden="true">📥</span>
                              <span className="dropdown-label flex-grow-1 position-relative">
                                {labels.inbox}
                                {unreadCount > 0 ? (
                                  <span className="position-absolute top-0 start-90 translate-middle badge rounded-pill bg-danger">
                                    {unreadBadgeText}
                                  </span>
                                ) : null}
                              </span>
                            </NavDropdown.Item>
                            <AdminNavMenu messages={headerMessages} />
                            <NavDropdown.Divider />
                          </>
                        ) : null}
                        <NavDropdown.Item
                          as={Link}
                          href={profilePath}
                          className="d-flex align-items-center gap-2"
                        >
                          <span aria-hidden="true">👤</span>
                          <span className="dropdown-label flex-grow-1">
                            {labels.profile}
                          </span>
                        </NavDropdown.Item>
                        <NavDropdown.Item
                          as="button"
                          onClick={handleLogout}
                          className="d-flex align-items-center gap-2"
                        >
                          <span aria-hidden="true">🚪</span>
                          <span className="dropdown-label flex-grow-1">
                            {labels.logout}
                          </span>
                        </NavDropdown.Item>
                      </NavDropdown>
                    ) : (
                      <button
                        type="button"
                        onClick={() =>
                          goToPath(
                            `/login?redirect=${encodeURIComponent(currentPath)}`,
                          )
                        }
                        className="nav-link d-flex align-items-center pe-0 bg-transparent border-0"
                      >
                        <span>
                          <Image
                            src="/icons/user-alt.png"
                            alt={labels.signIn}
                            width={34}
                            height={34}
                            className="login-icon"
                          />
                        </span>
                      </button>
                    )
                  ) : null}
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="shop-extras-cluster-lg"
                initial={{ opacity: 0, y: reduceMotion ? 0 : -6 }}
                animate={{ opacity: 1, y: 0, transition: { duration: 0.32 } }}
                exit={{ ...navItemVariants.exit }}
                layout
                className="d-flex align-items-center gap-2"
              >
                <SearchBtn
                  onClick={handleSearchClick}
                  showSearchInput={showSearchInput}
                  shouldScroll={pathname === '/shop'}
                  scrollTargetIds={[
                    'latest-products',
                    'search-results',
                    'latest-products-header',
                    'search-results-header',
                  ]}
                  ariaLabel={labels.searchTitle}
                />
                <Nav.Link
                  onClick={() => goToPath('/cart')}
                  className="d-flex align-items-center px-0 fs-6"
                  aria-label={cartLabel}
                  data-cart-anchor="true"
                >
                  <motion.img
                    src="/icons/cart.png"
                    alt=""
                    width={26}
                    height={26}
                    className="cart-icon"
                    initial={{ opacity: 0, y: reduceMotion ? 0 : 3 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2, ease: 'easeOut' }}
                  />
                  {cartCount > 0 ? (
                    <Badge pill bg="warning" className="mt-3">
                      {cartCount}
                    </Badge>
                  ) : null}
                </Nav.Link>

                {currentUser ? (
                  <NavDropdown
                    title={userLabel}
                    id="username"
                    className="ps-0 my-0 fs-6"
                  >
                    {isAdmin ? (
                      <>
                        <NavDropdown.Item
                          as={Link}
                          href="/admin/inbox"
                          className="d-flex align-items-center gap-2 "
                        >
                          <span aria-hidden="true">📥</span>
                          <span className="dropdown-label flex-grow-1 position-relative">
                            {labels.inbox}
                            {unreadCount > 0 ? (
                              <span className="position-absolute top-0 start-90 translate-middle badge rounded-pill bg-danger">
                                {unreadBadgeText}
                              </span>
                            ) : null}
                          </span>
                        </NavDropdown.Item>
                        <AdminNavMenu messages={headerMessages} />
                      </>
                    ) : null}
                    {isAdmin ? <NavDropdown.Divider /> : null}
                    <NavDropdown.Item
                      as={Link}
                      href={profilePath}
                      className="d-flex align-items-center gap-2"
                    >
                      <span aria-hidden="true">👤</span>
                      <span className="dropdown-label flex-grow-1">
                        {labels.profile}
                      </span>
                    </NavDropdown.Item>
                    <NavDropdown.Item
                      as="button"
                      onClick={handleLogout}
                      className="d-flex align-items-center gap-2"
                    >
                      <span aria-hidden="true">🚪</span>
                      <span className="dropdown-label flex-grow-1">
                        {labels.logout}
                      </span>
                    </NavDropdown.Item>
                  </NavDropdown>
                ) : (
                  <button
                    type="button"
                    onClick={() =>
                      goToPath(
                        `/login?redirect=${encodeURIComponent(currentPath)}`,
                      )
                    }
                    className="nav-link d-flex align-items-center pe-0 bg-transparent border-0"
                  >
                    <span>
                      <Image
                        src="/icons/user-alt.png"
                        alt={labels.signIn}
                        width={28}
                        height={28}
                        className="login-icon"
                      />
                    </span>{' '}
                  </button>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </Nav>
      </Navbar>

      {showConnectExtras && showSearchInput ? (
        <Offcanvas
          show={showSearchInput}
          onHide={() => setShowSearchInput(false)}
          placement="top"
          className="search-offcanvas search-offcanvas-lg bg-landscape-surface-2"
          backdrop
          backdropClassName="search-offcanvas-backdrop"
          aria-labelledby="search-offcanvas-title-lg"
        >
          <Offcanvas.Header className="pb-3" closeButton>
            <Offcanvas.Title id="search-offcanvas-title-lg">
              <h4 className="mb-0 text-primary">{labels.searchTitle}</h4>
            </Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body className="pt-2">
            <FilterBar
              categoriesTree={categoriesTree}
              pathname="/shop"
              onSubmit={() => setShowSearchInput(false)}
              onNavigate={() => setShowSearchInput(false)}
              filterMessages={categoriesMessages.filter || {}}
            />
            {logoutError ? (
              <div className="auth-message auth-message--error mt-3">
                {logoutError}
              </div>
            ) : null}
          </Offcanvas.Body>
        </Offcanvas>
      ) : null}
    </>
  );
}
